import { DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useState } from "react";
import { createPortal } from "react-dom";

import { createFormHelper } from "lib/Form";
import { QUESTION_ELEMENT_TEMPLATES } from "models";
import {
  AvailableQuestionElement,
  ELEMENTS_DROP_ZONE_ID,
  ElementsDropZone,
  QuestionElementCard
} from "modules/QuestionsDashboard/components/EditQuestionModal/components/QuestionElements/components";
import { useQuestionElementsQuery } from "modules/QuestionsDashboard/hooks";

import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import type { Question, QuestionElement } from "models";
import type { QuestionElementConfiguration } from "modules/QuestionsDashboard/util";

type QuestionElementsProps = {
  canEdit: boolean;
};

const FormHelper = createFormHelper<Question>();

const SIGNATURE_TEMPLATES = [QUESTION_ELEMENT_TEMPLATES.EmployeeSignature, QUESTION_ELEMENT_TEMPLATES.CustomerSignature];

const isSignature = (questionElement: QuestionElement) => SIGNATURE_TEMPLATES.includes(questionElement.template);

export const QuestionElements = ({ canEdit }: QuestionElementsProps) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 100 } })
  );

  const [draggedElement, setDraggedElement] = useState<QuestionElement | null>(null);

  const { setValue } = FormHelper.useFormContext();
  const questionElements = FormHelper.useWatch("question_elements") ?? [];
  const { data: availableQuestionElements = [] } = useQuestionElementsQuery();

  const updateQuestionElements = (updatedQuestionElements: QuestionElement[]) => setValue("question_elements", updatedQuestionElements, { shouldDirty: true });

  const handleMove = (fromIdx: number, toIdx: number) =>
    updateQuestionElements(questionElements.with(fromIdx, questionElements[toIdx]).with(toIdx, questionElements[fromIdx]));

  const handleChangeConfiguration = (elementIdx: number, configuration: QuestionElementConfiguration) =>
    updateQuestionElements(questionElements.with(elementIdx, { ...questionElements[elementIdx], default_configuration: JSON.stringify(configuration) }));

  const handleDragStart = ({ active }: DragStartEvent) => {
    setDraggedElement(availableQuestionElements.find(questionElement => questionElement.id === active.id) ?? null);
  };

  const handleDragEnd = ({ over }: DragEndEvent) => {
    setDraggedElement(null);
    if (over?.id !== ELEMENTS_DROP_ZONE_ID || !draggedElement) return;
    if (isSignature(draggedElement) && questionElements.some(isSignature)) return;

    updateQuestionElements([...questionElements, { ...draggedElement, ordering: questionElements.length }]);
  };

  return (
    <div className="QuestionElements">
      {questionElements.map((questionElement, elementIdx) => (
        <QuestionElementCard
          key={`question-element-${elementIdx}`}
          questionElement={questionElement}
          isFirst={elementIdx === 0}
          isLast={elementIdx === questionElements.length - 1}
          canEdit={canEdit}
          onMoveUp={() => handleMove(elementIdx, elementIdx - 1)}
          onMoveDown={() => handleMove(elementIdx, elementIdx + 1)}
          onRemove={() => updateQuestionElements(questionElements.toSpliced(elementIdx, 1))}
          onChangeConfiguration={configuration => handleChangeConfiguration(elementIdx, configuration)}
        />
      ))}

      {canEdit && (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setDraggedElement(null)}>
          <ElementsDropZone />

          <div className="available-question-elements">
            {availableQuestionElements.map(questionElement => (
              <AvailableQuestionElement key={questionElement.id} questionElement={questionElement} />
            ))}
          </div>

          {createPortal(
            <DragOverlay dropAnimation={null} zIndex={1001}>
              {draggedElement && (
                <div className="available-question-element">
                  <span>{draggedElement.name}</span>
                  <strong>{draggedElement.template}</strong>
                </div>
              )}
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      )}
    </div>
  );
};
